import { AlertBox, AlertBoxDismissButton, Box } from '@vp/swan';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CAContext } from '../../contexts/CAContext';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import ChatFooter from './ChatFooter/ChatFooter';
import ChatHeader from './ChatHeader/ChatHeader';
import ChatWindowContent from './ChatWindowContent/ChatWindowContent';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { IMessage } from '../../interfaces/component-interfaces';
import './ChatWindow.styles.scss';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import EndChatPage from '../EndChatPage/EndChatPage';
import PrechatFormMIAW from '../PrechatFormMIAW/PrechatFormMIAW';
import { IStartChatData } from '../../interfaces/api-interfaces-miaw';
import { useIdentity } from '@vp/auth-react';
import { IIdentityObject } from '../../interfaces/app-interfaces';
import { useCustomChatEvents } from '../../hooks/custom-hooks/useCustomChatEvents';
import { clearChatbotToken, clearChatSessionType, clearChatWindowState } from '../../utils/session-storage-utils';
import { sendEventToHelpAnywherePanel } from '../../utils/chat/help-panel-utils';
import { isOrderHelpPage, isStudioPage } from '../../utils/chat/page-utils';
import { useNRLogging } from '../../hooks/custom-hooks/useNRLogging';
import { useKeyboardAwareFooter } from './ChatFooter/useFooterRef';
import EndChatFeedback from '../EndChatPage/EndChatFeedback';
import { API_ENDPOINTS } from '../../config/api-config';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IChatWindowProps {}

const ChatWindow: React.FC<IChatWindowProps> = () => {
  const footerRef = useRef<HTMLDivElement>(null);

  const isKeyboardOpen = useKeyboardAwareFooter(footerRef);

  const { logCustomAttributesNR } = useNRLogging();
  const identity = useIdentity();
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [showEndChatPage, setShowEndChatPage] = useState(false);
  const [bannerText, setBannerText] = useState('');
  const [showBanner, setShowBanner] = useState(false);

  const { sendChatEvents } = useCustomChatEvents();
  const {
    chatState: { chatWindow, enableChatbot, chatSession, userInfo, chatButtonHandler, scrollToBottom, locale },
    setChatState,
  } = useContext(CAContext);

  const {
    state: {
      conversationMessages: agentMessages,
      startMiawChatSession,
      sendMiawChatMessage,
      attachmentErrorMessage,
      showAttachmentError,
      isMIAWApiError,
      isMIAWStartChatError,
    },
    setState: setMIAWContext,
  } = useContext(MiawChatContext);
  const {
    state: { conversationMessages: chatbotMessages, sendChatbotMessage, showErrorMessage, isSessionExpired },
    setState: setChatbotContext,
  } = useContext(ChatbotContext);

  const [conversationMessages, setConversationMessages] = useState<IMessage[]>();

  const startAgentChat = (data: IStartChatData) => {
    // Set User info which can be used to start MIAW chat session again for non logged in users
    if (!userInfo?.isLoggedIn) {
      setChatState((prevState) => ({
        ...prevState,
        userInfo: {
          isLoggedIn: false,
          userData: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            shopperId: (identity as IIdentityObject).canonicalId || '',
          },
        },
      }));
    }

    // Start MIAW chat session based on data from prechat form
    startMiawChatSession?.(data);
  };

  useEffect(() => {
    setConversationMessages([...chatbotMessages, ...agentMessages]);
  }, [agentMessages, chatbotMessages]);

  const onCloseChat = useCallback(() => {
    logCustomAttributesNR('ca-chatbot-info', {
      logName: 'close_button_clicked',
      logLevel: 'info',
      message: 'User clicked on close button from chat window',
    });
    console.log('Closing chat window');
    if (chatSession === 'miawChatSession' || chatSession === 'chatbotSession') {
      setShowEndChatPage(true);
    } else if (chatSession === 'initial' || chatSession === 'endChatSession') {
      if (isStudioPage()) {
        sendEventToHelpAnywherePanel('showHelpPanelButton');
        chatWindowHandler('hide');
        chatButtonHandler('hide');
      } else if (isOrderHelpPage()) {
        chatWindowHandler('hide');
        chatButtonHandler('hide');
        setChatState((prevState) => ({
          ...prevState,
          chatWindow: 'hidden',
          chatSession: 'initial',
        }));
      } else {
        sendChatEvents('onChatReady', {});
        onMinimize();
      }
      clearChatWindowState();
    } else {
      onMinimize();
    }
  }, [chatSession]);

  const onCancelEndChat = () => {
    setShowEndChatPage(false);
  };

  const onEndChatFlowComplete = () => {
    setShowEndChatPage(false);
    setChatState((prevState) => ({
      ...prevState,
      chatSession: 'initial',
    }));
  };

  const onSessionExpiredStartNewChat = () => {
    setChatState((prevState) => ({
      ...prevState,
      chatSession: 'initial',
      endChatInfo: undefined,
      isPrivacyPolicyAccepted: false,
    }));
    setChatbotContext((prev) => ({
      ...prev,
      isSessionExpired: false,
      conversationMessages: [],
      showErrorMessage: false,
      isChatbotEndError: false,
      customFlow: undefined,
    }));
    setMIAWContext((prev) => ({
      ...prev,
      conversationMessages: [],
      showErrorMessage: false,
      isMIAWApiError: false,
      conversationId: undefined,
      showAttachmentError: false,
    }));
    clearChatbotToken();
    clearChatSessionType();
  };

  /** Message will be read once end chat page is shown */
  useEffect(() => {
    if (showEndChatPage) {
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true })),
      }));
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true })),
      }));
    }
  }, [showEndChatPage]);

  const onMinimize = () => {
    setChatState((prevState) => ({
      ...prevState,
      chatWindow: 'minimized',
    }));
    sendChatEvents('onMinimize', {});
    logCustomAttributesNR('ca-chatbot-info', {
      logName: 'minimize_button_clicked',
      logLevel: 'info',
      message: 'User clicked on minimize button from chat window',
    });
  };

  const handleMessage = (data: string) => {
    if (chatSession === 'miawChatSession') {
      sendMiawChatMessage?.(data);
    } else {
      sendChatbotMessage?.({ type: 'text_message', messageData: data });
    }
  };

  useEffect(() => {
    const chatWindow = document.getElementById('chatWindowContainer');
    if (chatWindow) {
      if (isKeyboardOpen) {
        chatWindow.classList.add('chatWindowMobileKeyboard');
        scrollToBottom?.();
      } else {
        chatWindow.classList.remove('chatWindowMobileKeyboard');
        scrollToBottom?.();
      }
    }
  }, [isKeyboardOpen]);

  useEffect(() => {
    if (chatWindow === 'minimized') {
      setShowChatWindow(false);
    } else if (chatWindow === 'maximized') {
      setShowChatWindow(true);
      if (isStudioPage()) {
        sendEventToHelpAnywherePanel('hideChatLoader');
      }
    }
  }, [chatWindow]);

  const chatWindowHandler = useCallback((action: 'show' | 'hide') => {
    if (action === 'show') {
      setShowChatWindow(true);
    } else if (action === 'hide') {
      setShowChatWindow(false);
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.BANNER_CONFIG);

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        const matchedLocaleKey = Object.keys(data).find((key) => key.toLowerCase() === locale);
        if (matchedLocaleKey) {
          const localeConfig = data[matchedLocaleKey];
          if (localeConfig?.showBanner) {
            setBannerText(localeConfig.isChatbotBannerEnabled ? localeConfig.chatbotBannerText : localeConfig.bannerText);
            setShowBanner(true);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    setChatState((prevState) => ({
      ...prevState,
      chatWindowHandler,
    }));
  }, [chatWindowHandler]);

  /** Case when chatbot is not present, show prechat to start the MIAW chat */
  if (chatSession === 'initial' && !enableChatbot) {
    return (
      <Box className="chatWindowContainer" compactMode display={showChatWindow ? 'flex' : 'none'}>
        <ChatHeader onChatClose={onCloseChat} onChatMinimize={onMinimize} />
        <PrechatFormMIAW onSubmit={startAgentChat} />
      </Box>
    );
  }
  if (showEndChatPage && chatSession !== 'initial') {
    return (
      <Box className="chatWindowContainer" compactMode display={showChatWindow ? 'flex' : 'none'}>
        <ChatHeader onChatClose={onCloseChat} onChatMinimize={onMinimize} />
        <EndChatPage onCancelEnd={onCancelEndChat} onEndChatFlowComplete={onEndChatFlowComplete} />
        <ChatFooter onSendMessage={handleMessage} disableInput />
      </Box>
    );
  }
  /** Other cases like session continuation,refresh etc, render the chat window */
  return (
    <>
      <Box className={`chatWindowContainer`} compactMode display={showChatWindow ? 'flex' : 'none'} id="chatWindowContainer">
        <ChatHeader onChatClose={onCloseChat} onChatMinimize={onMinimize} />
        {isSessionExpired && chatSession === 'chatbotSession' ? (
          <EndChatFeedback isSessionExpired={isSessionExpired} onStartNewChat={onSessionExpiredStartNewChat} />
        ) : (
          <>
            {(showErrorMessage || isMIAWApiError || isMIAWStartChatError) && <ErrorMessage />}
            {showAttachmentError && (
              <Box className="AlertBoxContainer">
                <AlertBox skin="error">
                  {attachmentErrorMessage}
                  <AlertBoxDismissButton
                    accessibleText="Dismiss alert"
                    onClick={() => setMIAWContext((prev) => ({ ...prev, attachmentErrorMessage: '', showAttachmentError: false }))}
                  />
                </AlertBox>
              </Box>
            )}
            {showBanner && (
              <Box mb="5" p="5">
                <AlertBox skin="info">
                  {/* Always the chatbot banner text */}
                  {bannerText}
                  <AlertBoxDismissButton accessibleText="Dismiss alert" onClick={() => setShowBanner(false)} />
                </AlertBox>
              </Box>
            )}
            <ChatWindowContent conversationMessages={conversationMessages || []} />
          </>
        )}
        <ChatFooter onSendMessage={handleMessage} ref={footerRef} isKeyboardVisible={isKeyboardOpen} />
      </Box>
    </>
  );
};

export default ChatWindow;
